import PropTypes from 'prop-types';
import React from 'react';
import { List as list } from 'immutable';
import _ from 'lodash';

import { translate as t } from '../../lib/I18n';
import Station from '../../records/Station';
import StationStore from '../../stores/StationStore';
import recentStationsStore from '../../stores/RecentStationsStore';
import connectToStores from '../../helpers/connectToStores';
import { toImmutable, compact, union } from '../../helpers/Immutable';
import TrackingActions from '../../actions/TrackingActions';

import Autocomplete from '../form/Autocomplete';
import StationListItem from './StationListItem';
import FormError from '../form/Error';

const includes = (baseStr, testStr) => (baseStr || '').toLowerCase().includes((testStr || '').toLowerCase());

export class StationSelector extends React.PureComponent {

    static propTypes = {
        name: PropTypes.string,
        stationType: PropTypes.string,
        station: PropTypes.instanceOf(Station),
        placeholder: PropTypes.string,
        floatyPlaceholder: PropTypes.bool,
        className: PropTypes.string,
        onChange: PropTypes.func,
        debounce: PropTypes.bool,
        recentStationsList: PropTypes.instanceOf(list).isRequired,
        required: PropTypes.bool,
        label: PropTypes.node,
        title: PropTypes.string,
        error: PropTypes.string,
        customSearchService: PropTypes.func,
        overlayResults: PropTypes.bool,
        disabled: PropTypes.bool,
        focused: PropTypes.bool,
        filterByBrand: PropTypes.bool,
        excludeGroupStations: PropTypes.bool,
        isInvalid: PropTypes.bool,
        errorMessage: PropTypes.string,
        id: PropTypes.string,
    };

    static defaultProps = {
        name: '',
        station: null,
        placeholder: '',
        floatyPlaceholder: false,
        onChange: _.noop,
        className: '',
        stationType: null,
        debounce: true,
        recentStationsList: list(),
        required: false,
        label: null,
        error: null,
        customSearchService: null,
        overlayResults: false,
        disabled: false,
        focused: false,
        filterByBrand: false,
        excludeGroupStations: false,
        isInvalid: false,
        errorMessage: null,
        id: 'station'
    };

    state = {
        results: compact(toImmutable([ this.props.station ])),
        searchValue: null
    };

    UNSAFE_componentWillReceiveProps (newProps) {
        this.setState({ results: compact(toImmutable([ newProps.station ])) });
    }

    _doSearch = (value, stationType) => {
        const { customSearchService, filterByBrand } = this.props;
        return customSearchService
            ? customSearchService(value, stationType, filterByBrand)
            : StationStore.search(value, stationType, filterByBrand);
    };

    _handleSearch = async (searchValue = '') => {
        if (searchValue) {
            const results = await this._doSearch(searchValue, this.props.stationType);
            this.setState({ results, searchValue });
        } else {
            this.props.onChange(new Station());
        }
    };

    _handleClear = () => {
        this.props.onChange(new Station());
    };

    _handleSelect = (value) => {
        const { name } = this.props;
        const results = list().concat(this.state.results, this.props.recentStationsList);
        const stationResult = results.find((station) => station.nlc === value);
        TrackingActions.widgetStationSearch({ type: name, stationName: stationResult.name });
        this.setState({ results: toImmutable([ stationResult ])}, this.props.onChange.bind(null, stationResult));
    };

    _prepareResults (...results) {
        const { excludeGroupStations } = this.props;

        return union(...results).reduce((acc, data = {}) => {
            const { nlc: value, name: label } = data;

            if (excludeGroupStations && new Station(data).isGroupStation()) {
                return acc;
            }

            return value ? acc.concat({ value, label, data }) : acc;
        }, []);
    }

    _resultsFilter(inputValue, { data: { name = '', crs = '' } }) {
        return !inputValue || includes(name, inputValue) || includes(crs, inputValue);
    }

    render () {
        const {
            className,
            debounce,
            disabled,
            error,
            floatyPlaceholder,
            focused,
            label,
            title,
            name,
            overlayResults,
            placeholder,
            recentStationsList,
            required,
            station,
            isInvalid,
            errorMessage,
            id
        } = this.props;
        const { searchValue, results } = this.state;

        return (
            <div className="station-selector">
                <Autocomplete
                    name={ name }
                    placeholder={ placeholder }
                    floatyPlaceholder={ floatyPlaceholder }
                    defaultValue={ station ? station.nlc : null }
                    defaultInputValue={ station ? station.name : null }
                    results={ this._prepareResults(recentStationsList.reverse(), results) }
                    searchValue={ searchValue }
                    className={ className }
                    onSearch={ this._handleSearch }
                    onSelect={ this._handleSelect }
                    onClear={ this._handleClear }
                    optionElement= { StationListItem }
                    debounce={ debounce }
                    resultsFilter={ this._resultsFilter }
                    required={ required }
                    label={ label }
                    resultsLabel={ t('stationSelector.results.aria') }
                    title={ title }
                    overlayResults={ overlayResults }
                    disabled={ disabled }
                    focused={ focused }
                    isInvalid={ isInvalid }
                    errorMessage={ errorMessage }
                    id={ id }
                />
                { error && <FormError message={ error } /> }
            </div>
        );
    }

}


export default connectToStores([ recentStationsStore ], ({ stationType }) => ({
    recentStationsList: recentStationsStore.get(stationType)
}))(StationSelector);
